import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Table from "../../../components/Table/Table";
import { formatDateAndHour } from "../../../utils/formatDate";
import PageTitle from "../../../components/PageTitle/PageTitle";
import "./ShowLogs.scss";
import AlertModal from "../../../components/AlertModal/AlertModal";
import Button from "../../../components/Button/Button";
import Pagination from "../../../components/Pagination/Pagination";

const PAGE_SIZE = 20;

export default function ShowLogs() {
  const { axiosAuth } = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [usersById, setUsersById] = useState(new Map()); // id -> username
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);
  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: "", message: "" });
  }, []);

  // carregar utilizadores 1x (evita refetch a cada página)
  const fetchUsersOnce = useCallback(async () => {
    try {
      const res = await axiosAuth.get("/users/");
      const users = res.data || [];
      const idx = new Map(users.map((u) => [u.id, u.username]));
      setUsersById(idx);
    } catch (err) {
      openError(
        "Erro ao carregar utilizadores",
        err?.response?.data?.detail || err.message
      );
    }
  }, [axiosAuth, openError]);

  // carregar logs para uma página específica
  const fetchLogs = useCallback(
    async (page) => {
      setLoading(true);
      try {
        const limit = PAGE_SIZE;
        const offset = (page - 1) * limit;

        const res = await axiosAuth.get("/audit-logs/", {
          params: { limit, offset },
        });

        // const totalStr =
        //   res.headers?.["X-total-count"] ?? res.headers?.["X-Total-Count"];
        const total = Number(res.headers?.["x-total-count"] || 0);

        const data = res.data || [];
        setLogs(data);

        const pages = Math.max(1, Math.ceil(total / limit));
        setTotalPages(pages);

        // se por algum motivo o total diminuir e a página atual ficar fora do range,
        // recua para a última página válida
        if (page > pages) {
          setCurrentPage(pages);

          // refaz o fetch da última página válida
          const newOffset = (pages - 1) * limit;
          const res2 = await axiosAuth.get("/audit-logs/", {
            params: {
              limit,
              offset: newOffset,
            },
          });
          setLogs(res2.data || []);
        } else {
          setCurrentPage(page);
        }
      } catch (err) {
        setLogs([]);
        setTotalPages(1);
        openError(
          "Erro ao carregar logs",
          err?.response?.data?.detail || err.message
        );
      } finally {
        setLoading(false);
      }
    },
    [axiosAuth, openError]
  );

  useEffect(() => {
    // monta: carrega utilizadores e a primeira página de logs
    fetchUsersOnce();
    fetchLogs(1);
  }, [fetchUsersOnce, fetchLogs]);

  const handlePageChange = useCallback(
    (nextPage) => {
      if (nextPage < 1 || nextPage > totalPages) return;
      fetchLogs(nextPage);
    },
    [fetchLogs, totalPages]
  );

  const methodClass = (m) => {
    const up = (m || "").toUpperCase();
    if (up === "PUT") return "method-put";
    if (up === "DELETE") return "method-delete";
    if (up === "POST") return "method-post";
    return "method-other";
  };

  const statusClass = (code) => {
    const c = Number(code) || 0;
    if (c >= 200 && c < 300) return "status-2xx";
    if (c >= 300 && c < 400) return "status-3xx";
    if (c >= 400 && c < 500) return "status-4xx";
    if (c >= 500) return "status-5xx";
    return "status-other";
  };

  const columns = useMemo(
    () => [
      { key: "timestamp", label: "Data" },
      { key: "username", label: "Username" },
      {
        key: "action",
        label: "Método",
        render: (row) => (
          <span className={methodClass(row.method)}>{row.action}</span>
        ),
      },
      {
        key: "path",
        label: "Local",
        render: (row) => <span className="path">{row.path}</span>,
      },
      {
        key: "status",
        label: "Status",
        render: (row) => (
          <span className={statusClass(row.status_code)}>
            {row.status_code}
          </span>
        ),
      },
    ],
    []
  );

  const tableData = useMemo(
    () =>
      logs.map((l) => ({
        id: l.id,
        timestamp: formatDateAndHour(l.timestamp),
        username: usersById.get(l.user_id ?? -1) || "-",
        method: l.method,
        action: l.action,
        path: l.path,
        status_code: l.status_code,
      })),
    [logs, usersById]
  );

  return (
    <div className="show-logs-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      <PageTitle title="Atividade dos utilizadores" />
      <div className="logs-actions">
        <Button
          type="button"
          onClick={() => fetchLogs(currentPage)}
          disabled={loading}
        >
          Recarregar
        </Button>
      </div>

      <div className="container">
        <Table columns={columns} data={tableData} loading={loading} />
        {!loading && tableData.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      {!loading && tableData.length === 0 && (
        <div className="empty">Sem atividade registada.</div>
      )}
    </div>
  );
}
